import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, apiClient } from '../auth-lib';
import { Button, Input, Select, EmptyState, Skeleton } from '../design-system';
import { useNotifications } from '../context/NotificationContext';
import { isCockpitUser } from '../org-admin/components/navItems';
import OrgAdminLayout from '../org-admin/components/OrgAdminLayout';
import EmployeeLayout from '../employee/components/EmployeeLayout';
import { sectionLabel, SECTION_ORDER, ago, exactly } from '../lib/notificationDisplay';
import './Notifications.css';

// The full notification history: everything the bell dropdown shows, plus the
// search and the filters a dropdown has no room for.
//
// ⚠️ NO NAV ENTRY, AND THEREFORE NO `nav.*` GATE — reached only from the bell.
// That is not an oversight in `requiredNavKeyFor`, which answers `null` here and
// lets the route through: this feed is the caller's OWN rows and the API refuses
// to serve anyone else's (there is no "notifications for user X" endpoint at
// all), so there is no section-level question to ask. Every deep link out of a
// row still lands on a page that enforces its own.
//
// ⚠️ THE SHELL IS KEYED TO THE USER, NOT THE ROUTE — the rule `buildNavChrome`
// already follows. One route serves both audiences because everybody has a bell;
// hard-coding the cockpit layout would drop an employee into the firm's chrome,
// and a second `/employee/notifications` route would be the same page twice.

const PAGE_SIZE = 30;
const READ_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

function NotificationsPage() {
  const { openNotification, markAllRead, counts, revision } = useNotifications();

  // ⚠️ THE FILTERS LIVE IN THE URL, the `DocumentsBrowser` rule. A search worth
  // running is a search worth being able to go Back out of, and "the payment
  // ones from last month" is a link somebody will want to keep.
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const read = READ_FILTERS.some((f) => f.key === params.get('read')) ? params.get('read') : 'all';
  const section = params.get('section') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';

  const setParam = useCallback((key, value) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  }, [setParams]);

  // The box is local and the URL lags it: writing a history entry per keystroke
  // would make Back walk backwards through the word you just typed.
  const [draft, setDraft] = useState(q);
  useEffect(() => { setDraft(q); }, [q]);
  useEffect(() => {
    if (draft === q) return undefined;
    const t = setTimeout(() => setParam('q', draft.trim()), 300);
    return () => clearTimeout(t);
  }, [draft, q, setParam]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = useMemo(
    () => ({ limit: PAGE_SIZE, ...(q && { q }), ...(read !== 'all' && { read }),
             ...(section && { section }), ...(from && { from }), ...(to && { to }) }),
    [q, read, section, from, to]
  );

  // ⚠️ EVERY FILTER IS SENT TO THE SERVER, none is applied to `rows`. The list
  // is paged, so filtering what happens to be loaded would search the most
  // recent thirty rows and report "nothing found" for one sitting on page three.
  //
  // A request counter rather than an AbortController: two fetches racing (a
  // debounced search landing after a filter click) must not let the older one
  // win, and discarding its result is enough — the request itself is cheap.
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    setFailed(false);
    apiClient
      .get('/api/notifications', { params: query })
      .then((res) => {
        if (mine !== seq.current) return;
        setRows(res.data?.notifications || []);
        setHasMore(!!res.data?.hasMore);
        setNextBefore(res.data?.nextBefore || null);
      })
      .catch(() => {
        if (mine !== seq.current) return;
        setRows([]);
        setHasMore(false);
        setFailed(true);
      })
      .finally(() => { if (mine === seq.current) setLoading(false); });
    // `revision` is the provider's "something arrived" counter, so a
    // notification landing while this page is open shows up without a reload.
  }, [query, revision]);

  const loadMore = async () => {
    if (!hasMore || loadingMore || !nextBefore) return;
    setLoadingMore(true);
    const mine = seq.current;
    try {
      const res = await apiClient.get('/api/notifications', {
        params: { ...query, before: nextBefore },
      });
      if (mine !== seq.current) return;
      const page = res.data?.notifications || [];
      // De-duplicated on append, as the dropdown's pager is: a live arrival can
      // land between the request and its response and must not appear twice.
      setRows((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...page.filter((n) => !seen.has(n.id))];
      });
      setHasMore(!!res.data?.hasMore);
      setNextBefore(res.data?.nextBefore || null);
    } catch {
      /* leave the list as it stands; the button remains for a retry */
    } finally {
      setLoadingMore(false);
    }
  };

  // ⚠️ OPENING THIS PAGE MARKS NOTHING READ, unlike opening the bell. The
  // dropdown clears the badge because being shown the count IS receiving it;
  // here the read state is a thing you can filter on and sort your way through,
  // and a page that emptied its own Unread tab as you looked at it would be
  // useless for the one job it exists to do. Clearing is an explicit button.
  const open = (n) => {
    if (!n.readAt) {
      setRows((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
    }
    // The provider owns the API call, the badge and the navigation, so the bell
    // and this page can never disagree about what has been read.
    openNotification(n);
  };

  const clearAll = async () => {
    await markAllRead();
    const now = new Date().toISOString();
    setRows((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
  };

  const filtered = q || read !== 'all' || section || from || to;
  const clearFilters = () => setParams({}, { replace: true });

  // ── The filter popover ────────────────────────────────────────────────────
  //
  // ⚠️ THE THREE FILTERS USED TO SIT INLINE and, on a 393px phone, a section
  // select and two full-width date boxes pushed the first notification below
  // the fold — half the screen spent on controls nobody had touched yet. They
  // are behind a button now, at every width: one code path rather than two
  // layouts, and on a desktop a search box plus one button reads better than
  // four controls in a row.
  //
  // ⚠️ THE COUNT ON THE BUTTON IS WHAT MAKES HIDING THEM SAFE. A filter that is
  // on but out of sight is how somebody concludes the page is broken because
  // "their" notification is missing — so the button always says how many are
  // narrowing the list.
  const activeFilterCount = [section, from, to].filter(Boolean).length;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef(null);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onDown = (e) => {
      if (!filtersRef.current?.contains(e.target)) setFiltersOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setFiltersOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  return (
    <div className="notifs">
      <div className="notifs-bar">
        {/* Stays in the open at every width: it is the control people came for,
            and burying it behind the same button as the rest would cost a tap
            on the commonest action on the page. */}
        <Input
          className="notifs-search"
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search notifications…"
          aria-label="Search notifications"
        />

        <div className="notifs-filterwrap" ref={filtersRef}>
          <button
            type="button"
            className={`notifs-filterbtn${activeFilterCount ? ' notifs-filterbtn--on' : ''}`}
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-haspopup="dialog"
          >
            <span aria-hidden="true">⚙</span>
            Filters
            {activeFilterCount > 0 && (
              <span className="notifs-filterbtn-count">{activeFilterCount}</span>
            )}
          </button>

          {filtersOpen && (
            <div className="notifs-filterpop" role="dialog" aria-label="Filter notifications">
              <Select
                value={section}
                onChange={(e) => setParam('section', e.target.value)}
                label="Section"
              >
                <option value="">All sections</option>
                {SECTION_ORDER.map((key) => (
                  <option key={key} value={key}>{sectionLabel(key)}</option>
                ))}
              </Select>

              {/* Labelled because a bare date box says nothing about which end
                  of the range it is, and the pair is unusable guessed at. */}
              <Input
                label="From"
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setParam('from', e.target.value)}
              />
              <Input
                label="To"
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setParam('to', e.target.value)}
              />

              <div className="notifs-filterpop-actions">
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
                )}
                <Button size="sm" onClick={() => setFiltersOpen(false)}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="notifs-tabs" role="group" aria-label="Read state">
        {READ_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`notifs-tab${read === f.key ? ' notifs-tab--on' : ''}`}
            aria-pressed={read === f.key}
            onClick={() => setParam('read', f.key === 'all' ? '' : f.key)}
          >
            {f.label}
            {/* Only the unread tab carries a number, and it is the badge's own
                figure — a count beside "All" would be the length of a paged
                list, which is not a fact this page knows. */}
            {f.key === 'unread' && counts.total > 0 && (
              <span className="notifs-tab-count">{counts.total > 99 ? '99+' : counts.total}</span>
            )}
          </button>
        ))}

        <div className="notifs-tabs-right">
          {filtered && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>
          )}
          <Button variant="secondary" size="sm" onClick={clearAll} disabled={!counts.total}>
            Mark all read
          </Button>
        </div>
      </div>

      {loading && !rows.length && (
        <div className="notifs-list">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height="72px" />)}
        </div>
      )}

      {!loading && failed && (
        <EmptyState
          icon="⚠️"
          title="Could not load your notifications"
          description="The server did not answer. Check your connection and try again."
        />
      )}

      {!loading && !failed && !rows.length && (
        <EmptyState
          icon="🔔"
          title={filtered ? 'Nothing matches those filters' : 'Nothing yet'}
          description={
            filtered
              ? 'Try a different search, a wider date range, or clear the filters.'
              : 'Assignments, client requests, messages and announcements show up here.'
          }
          action={filtered ? <Button variant="secondary" onClick={clearFilters}>Clear filters</Button> : null}
        />
      )}

      {!!rows.length && (
        <div className="notifs-list">
          {rows.map((n) => (
            <button
              type="button"
              key={n.id}
              className={`notifs-row${n.readAt ? '' : ' notifs-row--unread'}`}
              onClick={() => open(n)}
            >
              <span className="notifs-row-dot" aria-hidden="true" />
              <span className="notifs-row-main">
                <span className="notifs-row-title">{n.title}</span>
                {n.body && <span className="notifs-row-body">{n.body}</span>}
                <span className="notifs-row-meta">
                  <span className="notifs-chip">{sectionLabel(n.section)}</span>
                  {n.actorName && <span>{n.actorName}</span>}
                  <time dateTime={n.createdAt} title={exactly(n.createdAt)}>{ago(n.createdAt)}</time>
                  {/* An announcement points nowhere by design, and a row that
                      looks like a link and goes nowhere reads as broken. */}
                  {!n.path && <span className="notifs-row-nolink">No linked page</span>}
                </span>
              </span>
            </button>
          ))}

          {hasMore && (
            <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load older'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const { user } = useAuth();
  const Shell = isCockpitUser(user) ? OrgAdminLayout : EmployeeLayout;
  return (
    <Shell title="Notifications" subtitle="Everything you have been told, searchable">
      <NotificationsPage />
    </Shell>
  );
}

export default Notifications;
